import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { surahs } from '../data/quranData';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const QURAN_API = 'https://api.alquran.cloud/v1';

// Bismillah pattern to strip from first ayah of surahs 2-114 (except 9)
// Note: Uses exact Unicode from api.alquran.cloud - diacritic order matters (shadda before fatha)
const BISMILLAH_PATTERN = /^بِسۡمِ ٱللَّهِ ٱلرَّحۡمَـٰنِ ٱلرَّحِیمِ\s*/;

const stripBismillah = (text) => text.replace(BISMILLAH_PATTERN, '').trim();

const SurahPage = () => {
  const { number } = useParams();
  const { t, language } = useLanguage();
  const [arabicData, setArabicData] = useState(null);
  const [translationData, setTranslationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const surahNum = parseInt(number, 10);
  const surahMeta = surahs.find(s => s.number === surahNum);
  const prevSurah = surahNum > 1 ? surahNum - 1 : null;
  const nextSurah = surahNum < 114 ? surahNum + 1 : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    const translationEdition = language === 'sv' ? 'sv.bernstrom' : 'en.asad';

    const fetchSurah = async () => {
      try {
        const [arabicRes, transRes] = await Promise.all([
          fetch(`${QURAN_API}/surah/${surahNum}`),
          fetch(`${QURAN_API}/surah/${surahNum}/${translationEdition}`)
        ]);

        if (!arabicRes.ok || !transRes.ok) throw new Error('API error');

        const arabicJson = await arabicRes.json();
        const transJson = await transRes.json();

        if (!cancelled) {
          setArabicData(arabicJson.data);
          setTranslationData(transJson.data);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchSurah();
    return () => { cancelled = true; };
  }, [surahNum, language]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [surahNum]);

  if (!surahMeta) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <p style={{ color: 'var(--text-secondary)' }}>Surah not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="surah-page">
      {/* Header */}
      <section className="py-10 islamic-pattern" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/quran"
              className="flex items-center gap-2 text-sm transition-colors hover:text-gold"
              style={{ color: 'var(--text-secondary)' }}
              data-testid="back-to-quran"
            >
              <ArrowLeft size={16} />
              {t('quran.backToList')}
            </Link>

            <div className="flex items-center gap-2">
              {prevSurah && (
                <Link
                  to={`/quran/${prevSurah}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-gold/10"
                  style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                  data-testid="prev-surah"
                >
                  <ChevronLeft size={16} />
                </Link>
              )}
              {nextSurah && (
                <Link
                  to={`/quran/${nextSurah}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-gold/10"
                  style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                  data-testid="next-surah"
                >
                  <ChevronRight size={16} />
                </Link>
              )}
            </div>
          </div>

          {/* Surah Info */}
          <div className="text-center">
            <div className="inline-block mb-4 px-4 py-1.5 rounded-full text-xs font-medium bg-gold/10 text-gold border border-gold/20">
              {language === 'sv'
                ? `Surah ${surahNum} - ${surahMeta.type === 'Meccan' ? t('quran.meccan') : t('quran.medinan')}`
                : `Surah ${surahNum} - ${surahMeta.type === 'Meccan' ? t('quran.meccan') : t('quran.medinan')}`}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }} data-testid="surah-name">
              {surahMeta.name}
            </h1>
            <p className="arabic-text text-gold text-3xl mb-2">{surahMeta.arabic}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {language === 'sv' ? surahMeta.sv : surahMeta.en} - {surahMeta.ayahs} Ayahs
            </p>
          </div>
        </div>
      </section>

      <div className="gold-divider"></div>

      {/* Verses */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20" data-testid="loading-indicator">
              <Loader2 size={32} className="text-gold animate-spin mb-4" />
              <p style={{ color: 'var(--text-muted)' }}>{t('quran.loading')}</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20" data-testid="error-message">
              <p className="text-red-400 mb-4">{t('quran.error')}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-outline text-sm px-6 py-2"
              >
                {language === 'sv' ? 'Forsok igen' : 'Try Again'}
              </button>
            </div>
          )}

          {!loading && !error && arabicData && (
            <>
              {/* Bismillah - shown for all surahs except Al-Fatihah (1) and At-Tawbah (9) */}
              {surahNum !== 1 && surahNum !== 9 && (
                <div className="text-center py-8 mb-6" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <p className="arabic-text text-2xl md:text-3xl text-gold opacity-80">
                    بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ
                  </p>
                </div>
              )}

              <div className="space-y-0" data-testid="verses-container">
                {arabicData.ayahs.map((ayah, index) => {
                  const translation = translationData?.ayahs?.[index];
                  // Strip Bismillah from first ayah of surahs 2-114 (except 9)
                  const shouldStripBismillah = index === 0 && surahNum !== 1 && surahNum !== 9;
                  const arabicText = shouldStripBismillah ? stripBismillah(ayah.text) : ayah.text;

                  return (
                    <div
                      key={ayah.numberInSurah}
                      className="verse-block"
                      data-testid={`verse-${ayah.numberInSurah}`}
                    >
                      {/* Verse number badge */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="verse-number">
                          {ayah.numberInSurah}
                        </span>
                      </div>

                      {/* Arabic text */}
                      <p className="arabic-text text-xl md:text-2xl leading-loose mb-4 text-gold">
                        {arabicText}
                      </p>

                      {/* Translation */}
                      {translation && (
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                          {translation.text}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Bottom navigation */}
              <div className="flex items-center justify-between mt-12 pt-8" style={{ borderTop: '1px solid var(--border-color)' }}>
                {prevSurah ? (
                  <Link
                    to={`/quran/${prevSurah}`}
                    className="flex items-center gap-2 text-sm transition-colors hover:text-gold"
                    style={{ color: 'var(--text-secondary)' }}
                    data-testid="bottom-prev"
                  >
                    <ChevronLeft size={16} />
                    {surahs[prevSurah - 1].name}
                  </Link>
                ) : <div />}
                {nextSurah ? (
                  <Link
                    to={`/quran/${nextSurah}`}
                    className="flex items-center gap-2 text-sm transition-colors hover:text-gold"
                    style={{ color: 'var(--text-secondary)' }}
                    data-testid="bottom-next"
                  >
                    {surahs[nextSurah - 1].name}
                    <ChevronRight size={16} />
                  </Link>
                ) : <div />}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default SurahPage;
